import React, { useEffect, useMemo, useState } from 'react'
import { Helmet } from 'react-helmet'
import moment from 'moment-timezone'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { ArrowLeftIcon, PencilSimpleIcon } from '@phosphor-icons/react'
import ClientModal from '../../components/ClientModal/ClientModal'
import Loader from '../../components/Loader/Loader'
import { getEngagementStatus } from '../../components/EngagementsTable/EngagementsTable'
import { toast } from '../../utils/toast'

import {
  useGetClientByIdQuery,
} from '../../redux/client.endpoints'
import { useLazyGetAllEngagementsQuery } from '../../redux/engagement.endpoints'
import { Client } from '../../types/Client'
import { User } from '../../types/User'

interface Props {
  user: User
}

const ClientDetails = ({ user }: Props) => {
  const { clientId } = useParams()
  const navigate = useNavigate()
  const [isModalOpen, setModalOpen] = useState(false)

  const { data: client, isLoading } = useGetClientByIdQuery({
    companyId: user.company.id,
    clientId: clientId ? parseInt(clientId) : 0,
  })

  const [getEngagements, { data: engagementsData = [] }] =
    useLazyGetAllEngagementsQuery()

  useEffect(() => {
    if (client?.company) {
      getEngagements({
        companyId: client.company.id,
        pageSize: 0,
        pageNumber: 0,
        sortBy: 'id',
        searchQuery: '',
        sortType: 'DESC',
      })
    }
  }, [client, getEngagements])

  const engagements = useMemo(
    () => engagementsData.filter(engagement => engagement.client?.id === client?.id),
    [engagementsData, client],
  )

  const isClientActive = useMemo(
    () => engagements.some(engagement => getEngagementStatus(engagement) === 'Active'),
    [engagements],
  )

  if (isLoading || !client) {
    return <Loader />
  }

  return (
    <div className="container my-4 client-detail">
      <Helmet title="Time Off - Client Details" />

      <Link to="/clients" className="client-detail__back-link">
        <ArrowLeftIcon size={14} weight="regular" />
        Back to Clients
      </Link>

      <div className="client-detail__header">
        <h1 className="client-detail__title">{client.name}&apos;s Details</h1>
        <button
          type="button"
          className="client-detail__edit-btn"
          onClick={() => setModalOpen(true)}
        >
          <PencilSimpleIcon size={14} weight="regular" color="#ffffff" />
          Edit Client
        </button>
      </div>

      <div className="client-detail__card">
        <div className="client-detail__field">
          <div className="client-detail__field-label">Description</div>
          <div className="client-detail__field-value">
            {client.description || '—'}
          </div>
        </div>
        <div className="client-detail__field">
          <div className="client-detail__field-label">Status</div>
          <div className="client-detail__field-value">
            <span
              className={`management-clients__badge management-clients__badge--${
                isClientActive ? 'active' : 'inactive'
              }`}
            >
              {isClientActive ? 'Active' : 'Inactive'}
            </span>
          </div>
        </div>
        <div className="client-detail__field">
          <div className="client-detail__field-label">Created At</div>
          <div className="client-detail__field-value client-detail__field-value--muted">
            {moment(client.created_at).format('MM/DD/YYYY')}
          </div>
        </div>
      </div>

      <div className="client-detail__engagements-header">
        <h2 className="client-detail__engagements-title">Engagements</h2>
        <span className="client-detail__engagements-count">
          ({engagements.length})
        </span>
      </div>

      {engagements.length === 0 ? (
        <div className="client-detail__empty">
          No engagements found for this client.
        </div>
      ) : (
        <div className="client-detail__table-card">
          <table className="client-detail__table">
            <thead>
              <tr>
                <th>#</th>
                <th>Status</th>
                <th>Team ID</th>
                <th>Team Code Name</th>
                <th>SOW Start Date</th>
                <th>SOW End Date</th>
              </tr>
            </thead>
            <tbody>
              {engagements.map((engagement, index) => {
                const status = getEngagementStatus(engagement)
                return (
                  <tr
                    key={engagement.id}
                    onClick={() => navigate(`/engagements/${engagement.id}`)}
                  >
                    <td>{index + 1}</td>
                    <td>
                      <span
                        className={`management-clients__badge management-clients__badge--${status.toLowerCase()}`}
                      >
                        {status}
                      </span>
                    </td>
                    <td>{engagement.team?.name}</td>
                    <td>{engagement.projectName}</td>
                    <td>{moment(engagement.startDate).format('MM/DD/YYYY')}</td>
                    <td>
                      {engagement.endDate
                        ? moment(engagement.endDate).format('MM/DD/YYYY')
                        : ''}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      <ClientModal
        isOpen={isModalOpen}
        mode="edit"
        client={client as Client}
        companyId={user.company.id}
        onClose={() => setModalOpen(false)}
        showActiveToggle={false}
      />
    </div>
  )
}

export default ClientDetails
